import {
    AppState,
    AuthenticationState,
    authLogout,
    authLogoutPath,
    ConfigurationContext,
    debugExtendedAccess,
    getAuthParams,
    getLoginUrl,
    getPageUrl,
    getRegisterUrl,
    getSubscribeUrl,
    isArticleLikeType,
    LogoutEvent,
    SubscribeClickOverrides,
} from '@news-mono/web-common'
import React, { useContext } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { BreachScreenContext } from '../../../../web-common/src/context-providers/BreachContextProvider'
import { useSectionMetaInfo } from '../../__helpers/use-section-meta'

declare const google: {
    accounts: {
        id: {
            disableAutoSelect: () => {}
        }
    }
}

export type OnSubscribeEventHandler = (
    overrides?: SubscribeClickOverrides,
) => void

export type AuthProviderOnEvent = (event: LogoutEvent) => void
export interface AuthenticationProviderContextValues {
    onLoginClick(): void
    onRegisterClick(): void
    onLogoutClick(onEvent: AuthProviderOnEvent): void
    onSubscribeClick: OnSubscribeEventHandler
    getSubscribeLink: (overrides?: SubscribeClickOverrides) => string
}

export const AuthenticationProviderContext = React.createContext<
    AuthenticationProviderContextValues | undefined
>(undefined)

export function useAuthActions() {
    const values = useContext(AuthenticationProviderContext)
    if (!values) {
        throw new Error(
            'Ensure <AuthenticationProvider /> is rendered above the component using this hook',
        )
    }

    return values
}

export const AuthenticationProvider: React.FC<React.PropsWithChildren<{}>> = ({
    children,
}) => {
    const dispatch = useDispatch()
    const config = useContext(ConfigurationContext)
    const sectionMeta = useSectionMetaInfo()
    const breachScreenContext = React.useContext(BreachScreenContext)
    const context = breachScreenContext.context
    const authentication = useSelector<AppState, AuthenticationState>(
        ({ authentication }) => authentication,
    )
    const location = useLocation()
    const pageUrl = getPageUrl(sectionMeta, config.publicUrl, location.pathname)

    const onLoginClick = () => {
        const loginUrl = getLoginUrl()
        window.location.href = loginUrl
    }

    const onLogoutClick = async (onEvent: AuthProviderOnEvent) => {
        dispatch(
            authLogout({
                onEvent,
            }),
        )
        debugExtendedAccess('Logging out %o', authentication)
        if (
            authentication.loginProvider === 'Google' ||
            authentication.socialProviders.match(/Google/)
        ) {
            // Sign out google user: https://developers.google.com/identity/gsi/web/guides/automatic-sign-in-sign-out#sign-out
            debugExtendedAccess('Logging out google user')
            google.accounts.id.disableAutoSelect()
            window.location.href = authLogoutPath
        } else {
            window.location.href = authLogoutPath
        }
    }

    function getSubscribeLink(overrides: SubscribeClickOverrides | undefined) {
        const auth = getAuthParams({ sectionMeta })
        const article =
            context?.publication && isArticleLikeType(context?.publication)
                ? context.publication
                : undefined

        const subscribeLink = getSubscribeUrl({
            config,
            auth,
            pageUrl,
            article,
            packagePath: overrides?.packagePath || context?.packagePath,
            source: sectionMeta.Hostname,
            productName: sectionMeta.Title,
            campaign: context?.breachCreativeName,
            callToAction: overrides?.callToAction,
            additionalParams: {
                utm_term: context?.breachDeal,
                utm_content: context?.publication?.id,
            },
            offerCode: overrides?.offerCode || context?.offerCode,
            returningUser: context?.returningUser,
            requiredAccessLevel: context?.requiredAccessLevel,
        })
        return subscribeLink
    }

    const onSubscribeClick: OnSubscribeEventHandler = (overrides) => {
        const subscribeLink = getSubscribeLink(overrides)

        window.location.href = subscribeLink
    }

    const onRegisterClick = () => {
        const registerUrl = getRegisterUrl()
        window.location.href = registerUrl
    }

    const values = {
        onLoginClick,
        onRegisterClick,
        onLogoutClick,
        onSubscribeClick,
        getSubscribeLink,
    }

    return (
        <AuthenticationProviderContext.Provider value={values}>
            {children}
        </AuthenticationProviderContext.Provider>
    )
}
